import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Filter } from "../../utils/icons";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.air1bet.com";

const BetHistoryModal = ({ show, onClose }) => {
  const [betHistory, setBetHistory] = useState({ transactions: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", status: "", transactionType: "" });

  const fetchBetHistory = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const uid = localStorage.getItem("uid");
      const token = localStorage.getItem("token");
      let t = token;
      try { t = JSON.parse(token); } catch {}

      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.dateFrom) params.append("date_from", filters.dateFrom);
      if (filters.dateTo) params.append("date_to", filters.dateTo);
      if (filters.status) params.append("transaction_status", filters.status);
      if (filters.transactionType) params.append("transaction_type", filters.transactionType);
      if (searchTerm) params.append("search", searchTerm);

      const res = await fetch(`${API_BASE}/betHistory/transactions/user/${uid}?${params}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setBetHistory(data);
    } catch {
      setBetHistory({ transactions: [], pagination: {} });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  useEffect(() => {
    if (show) fetchBetHistory(currentPage);
  }, [show, currentPage, fetchBetHistory]);

  // Debounced search
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => { setCurrentPage(1); fetchBetHistory(1); }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const formatDate = (d) => d ? new Date(d).toLocaleString() : "N/A";

  const handleSearch = (e) => { e.preventDefault(); setCurrentPage(1); fetchBetHistory(1); };
  const resetFilters = () => { setFilters({ dateFrom: "", dateTo: "", status: "", transactionType: "" }); setSearchTerm(""); setCurrentPage(1); };

  return (
    <AnimatePresence>
      {show && (
        <>
          <motion.div className="upm-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={onClose} />
          <div className="upm-center-wrapper" onClick={onClose}>
            <motion.div
              className="bh-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bh-header">
                <div className="bh-header-title">Bet History</div>
                <button className="upm-close" onClick={onClose}><X size={20} color="#fff" /></button>
              </div>

              {/* Body */}
              <div className="bh-body">
                {/* Search + Filter */}
                <div className="bh-search-row">
                  <form className="bh-search-form" onSubmit={handleSearch}>
                    <input
                      className="bh-search-input"
                      type="text"
                      placeholder="Search by game title, transaction ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button className="bh-search-btn" type="submit"><Search size={16} color="#fff" /></button>
                  </form>
                  <button className="bh-filter-toggle" onClick={() => setShowFilters((v) => !v)}>
                    <Filter size={16} color="#fff" />
                  </button>
                </div>

                {/* Filters */}
                {showFilters && (
                  <div className="bh-filters">
                    <div className="bh-filter-row">
                      <div className="bh-filter-col">
                        <label>From Date</label>
                        <input type="date" value={filters.dateFrom} onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))} />
                      </div>
                      <div className="bh-filter-col">
                        <label>To Date</label>
                        <input type="date" value={filters.dateTo} onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))} />
                      </div>
                    </div>
                    <div className="bh-filter-row">
                      <div className="bh-filter-col">
                        <label>Status</label>
                        <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                          <option value="">All</option>
                          <option value="SUCCESS">Success</option>
                          <option value="ERROR">Error</option>
                          <option value="PENDING">Pending</option>
                        </select>
                      </div>
                      <div className="bh-filter-col">
                        <label>Transaction Type</label>
                        <select value={filters.transactionType} onChange={(e) => setFilters((p) => ({ ...p, transactionType: e.target.value }))}>
                          <option value="">All</option>
                          <option value="BET">Bet</option>
                          <option value="WIN">Win</option>
                          <option value="REFUND">Refund</option>
                        </select>
                      </div>
                    </div>
                    <div className="bh-filter-actions">
                      <button className="bh-apply-btn" onClick={() => { setCurrentPage(1); fetchBetHistory(1); }}>Apply Filters</button>
                      <button className="bh-reset-btn" onClick={resetFilters}>Reset</button>
                    </div>
                  </div>
                )}

                {/* Content */}
                {loading ? (
                  <div className="bh-loading"><div className="txn-spinner" /><p>Loading bet history...</p></div>
                ) : betHistory.transactions.length === 0 ? (
                  <div className="bh-empty"><p>No bet history found</p></div>
                ) : (
                  <>
                    <div className="bh-table-wrap">
                      <table className="bh-table">
                        <thead>
                          <tr><th>Game</th><th>Amount</th><th>Type</th><th>Date</th></tr>
                        </thead>
                        <tbody>
                          {betHistory.transactions.map((bet) => (
                            <tr key={bet.id}>
                              <td>
                                <div className="bh-game-title">{bet.game_title}</div>
                                <div className="bh-vendor">{bet.game_vendor}</div>
                              </td>
                              <td>
                                <span className={`bh-amount ${bet.transaction_type === "WIN" ? "bh-amount--win" : bet.transaction_type === "LOSS" ? "bh-amount--loss" : ""}`}>
                                  {bet.transaction_type === "WIN" ? "+" : bet.transaction_type === "LOSS" ? "-" : ""}
                                  {bet.amount} {bet.currency_code}
                                </span>
                              </td>
                              <td><span className="bh-type-tag">{bet.transaction_type}</span></td>
                              <td className="bh-date">{formatDate(bet.transaction_timestamp)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="bh-pagination">
                      <span className="bh-page-info-text">Showing {betHistory.transactions.length} of {betHistory.pagination.totalCount} entries</span>
                      <div className="bh-page-controls">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</button>
                        <span>Page {currentPage} of {betHistory.pagination.totalPages}</span>
                        <button disabled={currentPage === betHistory.pagination.totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BetHistoryModal;
